<template>
  <button
    v-if="image"
    type="button"
    class="release-media release-media--image"
    :aria-label="t('releasenotes.enlarge')"
    @click="enlarged = true"
  >
    <img :src="image" alt="" loading="lazy" />
    <span class="release-media__zoom"><v-icon icon="mdi-magnify-plus-outline" size="18" /></span>
  </button>
  <div v-else class="release-media release-media--icon">
    <v-icon :icon="icon" :size="iconSize" color="primary" />
  </div>

  <v-dialog v-if="image" v-model="enlarged" width="auto">
    <button type="button" class="release-media__full" :aria-label="t('releasenotes.shrink')" @click="enlarged = false">
      <img :src="image" alt="" />
    </button>
  </v-dialog>
</template>

<script setup lang="ts">
/**
 * @fileOverview The still (or icon) of one release highlight, shared by the dialog and the changelog.
 *
 * Small beside the text, so a card stays a card and the dialog does not turn into a scroll of pictures;
 * a click shows the still at full size, which is where its text can be read. See docs/release-notes.md.
 */
import { ref } from "vue"
import { useI18n } from "vue-i18n"

withDefaults(defineProps<{ image?: string | null; icon: string; iconSize?: number }>(), {
  image: null,
  iconSize: 44,
})

const { t } = useI18n()
const enlarged = ref(false)
</script>

<style scoped>
.release-media {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 16 / 10;
  overflow: hidden;
  padding: 0;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 10px;
  background: rgba(var(--v-theme-primary), 0.06);
}

.release-media img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top left;
}

.release-media--image {
  cursor: zoom-in;
}

.release-media--image:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.release-media--icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Says "this opens" without covering the still; fully visible on hover and keyboard focus. */
.release-media__zoom {
  position: absolute;
  right: 6px;
  bottom: 6px;
  display: flex;
  padding: 4px;
  border-radius: 999px;
  color: rgb(var(--v-theme-on-surface));
  background: rgba(var(--v-theme-surface), 0.9);
  opacity: 0.75;
  transition: opacity 0.15s;
}

.release-media--image:hover .release-media__zoom,
.release-media--image:focus-visible .release-media__zoom {
  opacity: 1;
}

/* Fits the screen both ways: a still is 16:10, so on a laptop it is the height that runs out first. */
.release-media__full {
  display: block;
  margin: 0 auto;
  padding: 0;
  border: 0;
  border-radius: 12px;
  overflow: hidden;
  cursor: zoom-out;
  background: rgb(var(--v-theme-surface));
}

.release-media__full img {
  display: block;
  width: min(1200px, calc(100vw - 48px), calc((100vh - 48px) * 1.6));
  height: auto;
}
</style>
